import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { TextArea } from '@/UI/TextArea/TextArea';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { PlusIcon } from '@/UI/Icons/PlusIcon';

export const NAME_MAX = 120;
export const DESCRIPTION_MAX = 1000;
export const TAGS_MAX = 5;
// TODO: replace with the real category list once defined; for now we ship a single option.
export const CATEGORY_OPTIONS = [{ id: 'workflow', label: 'Weavy Workflow', value: 'workflow' }];

// TODO: replace with the real tag list once defined; for now we ship a sample set.
const SAMPLE_TAGS = ['3D', 'Batch Processing', 'Image Editing', 'Cinema', 'Tag 5'];

interface FieldLabelRowProps {
  label: string;
  counter?: string;
  required?: boolean;
  requiredError?: boolean;
}

export const FieldLabelRow = ({ label, counter, required = false, requiredError = false }: FieldLabelRowProps) => (
  <FlexCenVer sx={{ justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body-sm-rg" color={color.White64_T}>
      {label}
      {required && (
        <span aria-hidden style={requiredError ? { color: color.Weavy_Error } : undefined}>
          {' *'}
        </span>
      )}
    </Typography>
    {counter && (
      <Typography variant="body-sm-rg" color={color.White64_T}>
        {counter}
      </Typography>
    )}
  </FlexCenVer>
);

interface PublishToCommunityModalDescriptionProps {
  name: string;
  onNameChange: (v: string) => void;
  nameError: string | null;
  description: string;
  onDescriptionChange: (v: string) => void;
  descriptionError: string | null;
  category: string;
  onCategoryChange: (v: string) => void;
  tags: string[];
  onToggleTag: (tag: string) => void;
}

const PublishToCommunityModalDescription = ({
  name,
  onNameChange,
  nameError,
  description,
  onDescriptionChange,
  descriptionError,
  category,
  onCategoryChange,
  tags,
  onToggleTag,
}: PublishToCommunityModalDescriptionProps) => {
  const { t } = useTranslation();

  return (
    <>
      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.NAME_LABEL)}
          counter={`${name.length}/${NAME_MAX}`}
          required
          requiredError={Boolean(nameError)}
        />
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          size="large"
          fullWidth
          error={Boolean(nameError)}
        />
        {nameError && (
          <Typography variant="body-sm-rg" color={color.Weavy_Error} sx={{ mt: 0.5 }}>
            {nameError}
          </Typography>
        )}
      </FlexCol>

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_LABEL)}
          counter={`${description.length}/${DESCRIPTION_MAX}`}
          required
          requiredError={Boolean(descriptionError)}
        />
        <TextArea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t(
            I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_PLACEHOLDER,
          )}
          rows={3}
          error={Boolean(descriptionError)}
          inputProps={{ style: { resize: 'none' } }}
          sx={{ mt: 0 }}
        />
        {descriptionError && (
          <Typography variant="body-sm-rg" color={color.Weavy_Error} sx={{ mt: 0.5 }}>
            {descriptionError}
          </Typography>
        )}
      </FlexCol>

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.CATEGORY_LABEL)}
          required
        />
        <Dropdown
          value={category}
          options={CATEGORY_OPTIONS}
          onChange={(opt) => onCategoryChange(opt.value)}
          width="100%"
          matchTriggerWidth
          sx={{ height: 32 }}
        />
      </FlexCol>

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.TAGS_LABEL)}
        />
        <Flex sx={{ flexWrap: 'wrap', gap: 1 }}>
          {SAMPLE_TAGS.map((tag) => {
            const isSelected = tags.includes(tag);
            const atLimit = tags.length >= TAGS_MAX && !isSelected;
            return (
              <FlexCenVer
                key={tag}
                onClick={() => onToggleTag(tag)}
                sx={{
                  border: `1px solid ${color.White16_T}`,
                  borderRadius: 1,
                  pr: 1,
                  background: isSelected ? color.White04_T : color.Black92,
                  cursor: atLimit ? 'default' : 'pointer',
                  opacity: atLimit ? 0.5 : 1,
                  '&:hover': { background: atLimit ? color.Black92 : color.White04_T },
                }}
              >
                <FlexCenVer sx={{ width: 24, height: 24, justifyContent: 'center' }}>
                  <PlusIcon width={12} height={12} strokeWidth={2} />
                </FlexCenVer>
                <Typography variant="body-sm-rg">{tag}</Typography>
              </FlexCenVer>
            );
          })}
        </Flex>
      </FlexCol>
    </>
  );
};

export default PublishToCommunityModalDescription;
